/**
 * The billing "State" field can hold three shapes by the time it is sent:
 *  - the option the visitor picked: `{ value: 'US-FL', label: 'Florida' }`
 *  - the bare code an earlier order stored: `'US-FL'` (store restores it as an option, but
 *    older tabs and browser autofill write straight through v-model)
 *  - the typed or autofilled NAME: `'Florida'` — the select showed it, nothing matched it.
 * `billingForm.state.value` was read blindly, so the third shape sent no state_code and
 * order-api answered 422 "Missing data for required field" on a US address that looked complete.
 */
export interface StateOption { value: string; label: string }

export function stateCodeOf(state: any, options: StateOption[] = []): string | null {
  if (!state) return null
  if (typeof state === 'object') return state.value ? String(state.value) : null

  const text = String(state).trim()
  if (!text) return null
  const lower = text.toLowerCase()

  const match = options.find((o) => o.value?.toLowerCase() === lower)
    || options.find((o) => o.label?.toLowerCase() === lower)
    || options.find((o) => {
      const code = String(o.value || '')
      const dash = code.indexOf('-')
      return dash !== -1 && code.substring(dash + 1).toLowerCase() === lower
    })
  if (match) return String(match.value)

  // No list to check against: a short code-looking string is the best we have.
  return options.length === 0 && /^[A-Z0-9-]{1,6}$/i.test(text) ? text.toUpperCase() : null
}

export function stateOptionOf(state: any, options: StateOption[] = []): StateOption | null {
  const code = stateCodeOf(state, options)
  if (!code) return null
  return options.find((o) => String(o.value) === code) || (typeof state === 'object' && state?.value ? state : { value: code, label: code })
}
